import { type OptionProps, useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { type Book, CorporateTax } from '../models';
import { fetchBookSetting } from '../repositories';

interface UsedBook {
  key: number;
  bookName: string;
}

interface BookContextProps {
  books: Book[];
  usedBooks: UsedBook[];
  updateBooks: () => Promise<void>;
  bookOptions: OptionProps[];
  corporateTax: CorporateTax;
  hasFinanceBook: boolean;
  hasTaxBook: boolean;
}

const BookContext = createContext<BookContextProps>({
  books: [],
  usedBooks: [],
  updateBooks: async () => {},
  bookOptions: [],
  corporateTax: CorporateTax.init(),
  hasFinanceBook: false,
  hasTaxBook: false,
});

function BooksProvider({ children }: { children: ReactNode }) {
  const [books, setBooks] = useState<Book[]>([]);
  const [corporateTax, setCorporateTax] = useState<CorporateTax>(CorporateTax.init);
  const [isDateFetchingEnd, setIsDateFetchingEnd] = useState(false);
  const [hasFinanceBook, setHasFinanceBook] = useState(false);
  const [hasTaxBook, setHasTaxBook] = useState(false);
  const { tryRequest } = useTry();

  const updateBooks = useCallback(
    async () =>
      await tryRequest(async () => {
        const bookSetting = await fetchBookSetting();
        setBooks(bookSetting.books);
        setCorporateTax(bookSetting.corporateTax);
        setIsDateFetchingEnd(true);
        if (bookSetting.corporateTax.bookUsedAsFinanceBook) {
          setHasFinanceBook(true);
        } else {
          setHasFinanceBook(false);
        }

        if (bookSetting.corporateTax.bookUsedAsTaxBook) {
          setHasTaxBook(true);
        } else {
          setHasTaxBook(false);
        }
      }),
    [],
  );

  useEffect(() => {
    void updateBooks();
  }, [updateBooks]);

  return (
    <BookContext.Provider
      value={{
        books,
        usedBooks: books.filter(({ isUsed }) => isUsed),
        updateBooks,
        bookOptions: books
          .filter(({ isUsed }) => isUsed)
          .map((val) => {
            return {
              optionValue: val.key.toString(),
              displayValue: `${val.key.toString()} ${val.bookName}`,
            };
          }),
        corporateTax,
        hasFinanceBook,
        hasTaxBook,
      }}
    >
      {isDateFetchingEnd ? children : null}
    </BookContext.Provider>
  );
}

function useBooks() {
  return useContext(BookContext);
}

export type { UsedBook };
export { BooksProvider, useBooks };
